import { today } from '@seen/core/dates'
import { CoverageType, ExtendedCoverage } from '@seen/core/models/coverage'
import type { JSX } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import {
  CoverageForm,
  type CoverageFormValues,
} from '#src/components/coverage/CoverageForm.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { toast } from '#src/utils/toast.ts'

interface CoverageDialogProps {
  coverage?: ExtendedCoverage
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CoverageDialog({
  coverage,
  open,
  onOpenChange,
}: CoverageDialogProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const isEditing = !!coverage
  const participant = useCurrentParticipant()
  const createMutation = useCreateMutation<ExtendedCoverage>()
  const patchMutation = usePatchMutation<ExtendedCoverage>()
  const deletePatchMutation = usePatchMutation<ExtendedCoverage>()
  const openDeleteConfirmation = useDeleteConfirmationStore(
    (state) => state.open
  )

  const handleSubmit = (values: CoverageFormValues): void => {
    if (isEditing) {
      patchMutation.mutate(
        {
          instance: coverage,
          mutateFn: (c) => {
            c.type = values.type
            c.startDate = values.startDate
            if (c.endDate || values.endDate) {
              c.endDate = values.endDate ?? undefined
            }
            c.subscriberId = values.subscriberId ?? undefined
            c.subscriber = c.beneficiary
          },
        },
        { onSuccess: () => onOpenChange(false) }
      )
    } else {
      if (!participant) {
        throw new Error('Cannot add coverage: Participant not found')
      }
      const newCoverage = new ExtendedCoverage({
        type: values.type,
        startDate: values.startDate,
        endDate: values.endDate ?? undefined,
        participant: participant.reference,
        subscriberId: values.subscriberId ?? undefined,
      })
      createMutation.mutate(newCoverage, {
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const handleDelete = (): void => {
    if (!coverage) {
      return
    }
    openDeleteConfirmation({
      title: 'Delete Coverage',
      description:
        'Are you sure you want to delete this coverage, which will invalidate it permanently? If the coverage was valid for a period of time you should not delete it but instead set the correct start and end dates.',
      deleteText: 'Delete',
      onConfirm: () => {
        void (async () => {
          try {
            await deletePatchMutation.mutateAsync({
              instance: coverage,
              mutateFn: (c) => {
                c.status = 'entered-in-error'
              },
            })
            onOpenChange(false)
          } catch (error) {
            const message =
              error instanceof Error ? error.message : 'Unknown error'
            toast.error(`Failed to delete coverage: ${message}`)
          }
        })()
      },
    })
  }

  const defaultValues: CoverageFormValues = isEditing
    ? {
        type: coverage.type ?? CoverageType.MEDICARE,
        startDate: coverage.startDate ?? today(),
        endDate: coverage.endDate ?? null,
        subscriberId: coverage.subscriberId ?? null,
      }
    : {
        type: CoverageType.MEDICARE,
        startDate: today(),
        endDate: null,
        subscriberId: null,
      }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay>
        <DialogHeader className="py-4 text-lg">
          {isEditing ? 'Edit Coverage' : 'Add Coverage'}
        </DialogHeader>
        <DialogContent className="pt-0">
          <CoverageForm
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            submitLabel={isEditing ? 'Save Changes' : 'Add Coverage'}
            isSubmitting={
              isEditing ? patchMutation.isPending : createMutation.isPending
            }
            isEditing={isEditing}
            onDelete={isEditing ? handleDelete : undefined}
            isDeleting={deletePatchMutation.isPending}
            disabled={isReadOnly}
          />
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}
